import { Fragment, FormEvent, ReactNode, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import QRPrintModal from '../components/QRPrintModal';
import { Link } from 'react-router-dom';
import ClienteUnificado from './ClienteUnificado';
import Ativos from './Ativos';
import PmocConsulta from './PmocConsulta';

// pode_excluir: só vem preenchido quando a permissão "EXCLUIR UNIDADE/PISO/LOCAL
// SEM HISTÓRICO" está ligada. Hoje a exclusão é livre (apaga só o registro), então
// o normal é vir true/ausente; se o backend mandar false, a tela ainda respeita e
// desabilita o Excluir.
interface Unidade { id: number; nome: string; endereco: string | null; status: string; pode_excluir?: boolean; }
// unidade_excluida / piso_excluido: vêm do backend (?incluir_orfaos=1) = 1 quando o
// pai foi excluído. O item continua no banco e pode ser vinculado a outro pai.
interface Piso { id: number; unidade_id: number; nome: string; status: string; pode_excluir?: boolean; unidade_excluida?: number | boolean; }
interface Local { id: number; unidade_id: number; piso_id: number; nome: string; qr_code: string | null; status: string; pode_excluir?: boolean; piso_excluido?: number | boolean; unidade_excluida?: number | boolean; }
interface Categoria { id: number; nome: string; tipo: string; status: string; }
interface Tecnico {
  id: number; nome: string; email: string; telefone: string | null; whatsapp: string | null; status: string; unidades: number[]; valor_hora?: number | null;
}
interface Cliente {
  id: number; nome: string; documento: string | null; email: string | null; telefone: string | null; whatsapp: string | null; status: string;
}

type Aba = 'unidades' | 'pisos' | 'locais' | 'tipos_chamado' | 'tecnicos' | 'clientes' | 'log_auditoria';

const ABAS: Array<{ id: Aba; label: string }> = [
  { id: 'unidades', label: 'Unidades' },
  { id: 'pisos', label: 'Pisos' },
  { id: 'locais', label: 'Locais' },
  { id: 'tipos_chamado', label: 'Tipos de chamado' },
  // Aba "Técnicos" removida daqui: cadastro/gestão de técnico foi movido para /app/usuarios.
  // (Reversível: basta readicionar a linha abaixo.)
  // { id: 'tecnicos', label: 'Técnicos' },
  { id: 'clientes', label: 'Clientes' },
  // Só gestor/supervisor com "EXCLUIR UNIDADE/PISO/LOCAL SEM HISTÓRICO" (ver abasVisiveis).
  { id: 'log_auditoria', label: 'Log Auditoria' },
];

// Perfis "gestor/supervisor" — mesma lista de usuarioEhGestorOuSupervisor (core/auth.php).
const PERFIS_GESTOR_SUPERVISOR = ['admin_geral', 'admin_empresa', 'supervisor'];

interface LogAuditoria {
  id: number;
  entidade: 'unidades' | 'pisos' | 'locais';
  entidade_id: number | null;
  nome: string | null;
  unidade_nome: string | null;
  piso_nome: string | null;
  // OS vinculadas ao cadastro no momento da exclusão (logs novos; só informativo).
  os_vinculadas?: Array<{ id: number; codigo: string | null }>;
  // Logs ANTIGOS (regra de cascata): OS que saíram do relatório.
  os_desvinculadas: Array<{ id: number; codigo: string | null }>;
  // Pisos/locais que saíram junto com a unidade/piso excluído.
  excluidos_junto: Array<{ entidade: LogAuditoria['entidade']; id: number; nome: string | null }>;
  restaurado: boolean;
  excluido_em: string;
}

const ROTULO_ENTIDADE_LOG: Record<LogAuditoria['entidade'], string> = {
  unidades: 'Unidade',
  pisos: 'Piso',
  locais: 'Local',
};

// Aba "Log Auditoria": unidades/pisos/locais excluídos pelo próprio usuário, com
// as OS que estavam vinculadas e o botão Restaurar. Registros antigos (regra de
// cascata) ainda podem trazer pisos/locais em "excluidos_junto".
function LogAuditoriaSecao() {
  const [lista, setLista] = useState<LogAuditoria[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [restaurandoId, setRestaurandoId] = useState<number | null>(null);
  const paginacao = useListaPaginada(lista, 20);

  function carregar() {
    return api<LogAuditoria[]>('/cadastros/log_auditoria.php')
      .then(setLista)
      .catch((e) => setErro(erroMsg(e)))
      .finally(() => setCarregando(false));
  }

  useEffect(() => {
    carregar();
  }, []);

  // Desfaz a exclusão: o cadastro volta a aparecer nas abas. Logs antigos (regra
  // de cascata) também trazem de volta os pisos/locais que saíram junto.
  async function restaurar(r: LogAuditoria) {
    const nome = r.nome || '#' + r.entidade_id;
    const junto = r.excluidos_junto.length ? ' Os pisos/locais excluídos junto também voltam.' : '';
    if (!window.confirm(`Restaurar ${ROTULO_ENTIDADE_LOG[r.entidade] || r.entidade} "${nome}"? O cadastro volta a aparecer nas abas de Cadastros.${junto}`)) return;
    setErro('');
    setMsg('');
    setRestaurandoId(r.id);
    try {
      await api('/cadastros/log_auditoria.php', { method: 'POST', body: { log_id: r.id } });
      setMsg(`${ROTULO_ENTIDADE_LOG[r.entidade] || r.entidade} "${nome}" restaurado. Clique em Atualizar para ver nas outras abas.`);
      await carregar();
    } catch (e) {
      setErro(erroMsg(e));
    } finally {
      setRestaurandoId(null);
    }
  }

  return (
    <div>
      <div className="msg-ok" style={{ marginBottom: 14 }}><strong>LOG PARA REGISTRO</strong></div>
      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}
      <Tabela colunas={['Excluído em', 'Tipo', 'Nome', 'Unidade / Piso', 'OS vinculadas', '']}>
        {paginacao.itens.map((r) => (
          <tr key={r.id}>
            <td>{new Date(r.excluido_em).toLocaleString('pt-BR')}</td>
            <td>{ROTULO_ENTIDADE_LOG[r.entidade] || r.entidade}</td>
            <td>
              <strong>{r.nome || (r.entidade_id ? '#' + r.entidade_id : '-')}</strong>
              {r.excluidos_junto.length > 0 && (
                <div className="muted" style={{ fontSize: 12 }}>
                  Junto: {r.excluidos_junto.map((f) => `${ROTULO_ENTIDADE_LOG[f.entidade] || f.entidade} ${f.nome || '#' + f.id}`).join(', ')}
                </div>
              )}
            </td>
            <td>{[r.unidade_nome, r.piso_nome].filter(Boolean).join(' / ') || '-'}</td>
            <td>
              {(() => {
                // Log novo: os_vinculadas; log antigo (cascata): os_desvinculadas.
                const os = r.os_vinculadas?.length ? r.os_vinculadas : (r.os_desvinculadas || []);
                return os.length ? os.map((o) => o.codigo || '#' + o.id).join(', ') : '-';
              })()}
            </td>
            <td className="acoes">
              {r.restaurado ? (
                <span className="badge">Restaurado</span>
              ) : (
                <button
                  className="btn sec sm"
                  disabled={restaurandoId !== null}
                  onClick={() => restaurar(r)}
                >
                  {restaurandoId === r.id ? 'Restaurando...' : 'Restaurar'}
                </button>
              )}
            </td>
          </tr>
        ))}
        {!carregando && !lista.length && <tr><td colSpan={6} className="vazio">Nenhuma exclusão registrada por você.</td></tr>}
        {carregando && <tr><td colSpan={6} className="vazio">Carregando...</td></tr>}
      </Tabela>
      <PaginacaoLista {...paginacao} />
    </div>
  );
}

function erroMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// Cadastro NÃO se exclui: OS, equipamentos e relatórios antigos apontam para ele.
// Desativar preserva o histórico e apenas tira o item das novas seleções.
const ativo = (status?: string | null): boolean => (status || 'ativo') === 'ativo';

// "Órfão" = o pai foi excluído (exclusão sem cascata). Piso sem unidade / local sem
// piso (ou cujo piso está sem unidade) aparecem em Cadastros para reaproveitar.
const flag = (v: unknown): boolean => v === true || Number(v) === 1;
const pisoOrfao = (p: Piso): boolean => flag(p.unidade_excluida);
const localOrfao = (l: Local): boolean => flag(l.piso_excluido) || flag(l.unidade_excluida);

function BadgeStatus({ status }: { status?: string | null }) {
  const on = ativo(status);
  return (
    <span
      className="badge"
      style={on
        ? { background: '#f0fdf4', color: '#166534', border: '1px solid #16a34a33' }
        : { background: '#fef2f2', color: '#b91c1c', border: '1px solid #dc262633' }}
    >
      {on ? 'Ativo' : 'Inativo'}
    </span>
  );
}

// Exclusão agora é livre (apaga só o registro), então o selo só aparece se o
// backend mandar pode_excluir === false — compatibilidade caso volte a bloquear.
// Cor neutra de propósito: o vermelho fica só no botão Excluir.
function BadgePodeExcluir({ podeExcluir }: { podeExcluir?: boolean }) {
  if (podeExcluir !== false) return null;
  return (
    <span
      className="badge"
      style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e133' }}
      title="O sistema não liberou a exclusão deste cadastro — use Desativar."
    >
      Exclusão bloqueada
    </span>
  );
}

// Botão único de Desativar/Ativar (PUT status) — substitui o antigo "Excluir".
function BotaoStatus({
  status,
  entidade,
  onAlternar,
}: {
  status?: string | null;
  entidade: string;
  onAlternar: (proximo: string) => void;
}) {
  const on = ativo(status);
  const proximo = on ? 'inativo' : 'ativo';
  return (
    <button
      className={`btn ${on ? 'danger' : 'sec'} sm`}
      title={on
        ? `Desativar: deixa de aparecer em novos cadastros e novas OS, mas o histórico é preservado`
        : `Reativar ${entidade}`}
      onClick={() => {
        const acao = on ? 'desativar' : 'reativar';
        if (!window.confirm(`Deseja ${acao} ${entidade}?`)) return;
        onAlternar(proximo);
      }}
    >
      {on ? 'Desativar' : 'Ativar'}
    </button>
  );
}

// Botão "Excluir" — só aparece com a permissão de empresa "EXCLUIR
// UNIDADE/PISO/LOCAL SEM HISTÓRICO" ligada. Apaga SÓ aquele registro: pisos,
// locais, equipamentos e OS vinculados ficam intactos e as OS continuam no
// relatório. A exclusão fica no Log Auditoria, de onde pode ser restaurada.
function BotaoExcluir({ entidade, podeExcluir = true, onExcluir, aviso }: {
  entidade: string; podeExcluir?: boolean; onExcluir: () => Promise<void>;
  // O que acontece com os filhos (ex.: pisos/locais continuam para reaproveitar).
  aviso?: string;
}) {
  // Compatibilidade: se o backend mandar pode_excluir === false, respeita.
  if (!podeExcluir) {
    return (
      <button
        className="btn sec sm"
        disabled
        title={`O sistema não liberou a exclusão de ${entidade}. Use Desativar.`}
      >
        Excluir
      </button>
    );
  }
  return (
    <button
      className="btn danger sm"
      title={`Excluir apenas ${entidade}. ${aviso || 'Equipamentos e ordens de serviço vinculados NÃO são apagados e as OS continuam no relatório.'}`}
      onClick={() => {
        if (!window.confirm(`Excluir ${entidade}? Apenas este cadastro será excluído. ${aviso ? aviso + ' ' : ''}Equipamentos e ordens de serviço vinculados NÃO são apagados e as OS continuam no relatório. A exclusão fica registrada em Log Auditoria e pode ser restaurada por lá.`)) return;
        onExcluir();
      }}
    >
      Excluir
    </button>
  );
}

function nomeUnidade(lista: Unidade[], id: number): string {
  return lista.find((u) => u.id === Number(id))?.nome || '-';
}

function nomePiso(lista: Piso[], id: number): string {
  return lista.find((p) => p.id === Number(id))?.nome || '-';
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoeda = (v: unknown): string => num(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const valorMoeda = (v: unknown): string => 'R$ ' + fmtMoeda(v);

const parseMoedaInput = (raw: string): number => {
  const digitos = raw.replace(/\D/g, '');
  return digitos ? Number(digitos) / 100 : 0;
};

const ITENS_POR_PAGINA = 5;

function useListaPaginada<T>(lista: T[], itensPorPagina: number = ITENS_POR_PAGINA) {
  const [paginaAtual, setPaginaAtual] = useState(1);
  const totalPaginas = Math.max(1, Math.ceil(lista.length / itensPorPagina));
  const paginaSegura = Math.min(paginaAtual, totalPaginas);
  const inicioPagina = (paginaSegura - 1) * itensPorPagina;
  const itens = lista.slice(inicioPagina, inicioPagina + itensPorPagina);

  useEffect(() => {
    if (paginaAtual > totalPaginas) {
      setPaginaAtual(totalPaginas);
    }
  }, [paginaAtual, totalPaginas]);

  return { itens, paginaAtual: paginaSegura, setPaginaAtual, totalPaginas, total: lista.length, itensPorPagina };
}

function PaginacaoLista({
  paginaAtual,
  setPaginaAtual,
  totalPaginas,
  total,
  itensPorPagina,
}: {
  paginaAtual: number;
  setPaginaAtual: (value: number | ((atual: number) => number)) => void;
  totalPaginas: number;
  total: number;
  itensPorPagina: number;
}) {
  if (total <= itensPorPagina) return null;
  return (
    <div className="paginacao-lista">
      <span>Página {paginaAtual} de {totalPaginas}</span>
      <div className="paginacao-acoes">
        <button className="btn sec sm" disabled={paginaAtual <= 1} onClick={() => setPaginaAtual(1)}>Primeira</button>
        <button className="btn sec sm" disabled={paginaAtual <= 1} onClick={() => setPaginaAtual((atual) => Math.max(1, atual - 1))}>Anterior</button>
        <button className="btn sec sm" disabled={paginaAtual >= totalPaginas} onClick={() => setPaginaAtual((atual) => Math.min(totalPaginas, atual + 1))}>Próxima</button>
        <button className="btn sec sm" disabled={paginaAtual >= totalPaginas} onClick={() => setPaginaAtual(totalPaginas)}>Última</button>
      </div>
    </div>
  );
}

export default function Cadastros() {
  const { isOsSimplificadaDisponivel } = useAuth();
  if (isOsSimplificadaDisponivel) return <CadastrosSimplificado />;
  return <CadastrosPadrao />;
}

type AbaSimplificada = 'clientes' | 'tipos_chamado' | 'equipamentos' | 'pmoc';

function CadastrosSimplificado() {
  // "RETIRAR TELA PMOC": a aba sai daqui (a API do PMOC tambem barra).
  const { isSemPmoc } = useAuth();
  const [aba, setAba] = useState<AbaSimplificada>('clientes');
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [clienteAberto, setClienteAberto] = useState<number | null | undefined>(undefined);
  const [buscaCliente, setBuscaCliente] = useState('');
  // A lista traz ativos E inativos (incluir_inativos=1) porque reativar exige ver
  // o inativo. O filtro deixa escolher o recorte em vez de misturar sempre.
  const [statusCliente, setStatusCliente] = useState<'todos' | 'ativo' | 'inativo'>('todos');
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const termoCliente = buscaCliente.trim().toLowerCase();
  const clientesFiltrados = clientes.filter((c) => {
    if (statusCliente !== 'todos' && (c.status || 'ativo') !== statusCliente) return false;
    if (!termoCliente) return true;
    return [c.nome, c.email, c.documento, c.whatsapp, c.telefone]
      .some((v) => (v || '').toLowerCase().includes(termoCliente));
  });
  const totalAtivos = clientes.filter((c) => (c.status || 'ativo') === 'ativo').length;
  const totalInativos = clientes.length - totalAtivos;
  const clientesOrdenados = [...clientesFiltrados].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' }));
  const paginacaoClientes = useListaPaginada(clientesOrdenados);

  async function carregar() {
    setErro('');
    const [cats, cli] = await Promise.all([
      api<Categoria[]>('/cadastros/categorias.php?incluir_inativos=1'),
      api<Cliente[]>('/cadastros/clientes.php?incluir_inativos=1'),
    ]);
    setCategorias(cats);
    setClientes(cli);
  }

  useEffect(() => {
    carregar().catch((e) => setErro(erroMsg(e)));
  }, []);

  async function executar(fn: () => Promise<void>, sucesso: string) {
    setErro('');
    setMsg('');
    try {
      await fn();
      setMsg(sucesso);
      await carregar();
    } catch (e) {
      setErro(erroMsg(e));
    }
  }

  if (clienteAberto !== undefined) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
          <button className="btn sm" onClick={() => { setClienteAberto(undefined); carregar().catch((e) => setErro(erroMsg(e))); }}>Voltar para lista</button>
        </div>
        <ClienteUnificado
          clienteIdInicial={clienteAberto}
          onVoltar={() => {
            setClienteAberto(undefined);
            carregar().catch((e) => setErro(erroMsg(e)));
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="titulo-pg">
        <h1>Cadastros</h1>
        <button className="btn sec sm" onClick={() => carregar().catch((e) => setErro(erroMsg(e)))}>Atualizar</button>
      </div>

      <div className="tabs" style={{ marginBottom: 14 }}>
        <button className={aba === 'clientes' ? 'on' : ''} onClick={() => setAba('clientes')}>Clientes</button>
        <button className={aba === 'tipos_chamado' ? 'on' : ''} onClick={() => setAba('tipos_chamado')}>Tipos de chamado</button>
        <button className={aba === 'equipamentos' ? 'on' : ''} onClick={() => setAba('equipamentos')}>Equipamentos</button>
        {!isSemPmoc && <button className={aba === 'pmoc' ? 'on' : ''} onClick={() => setAba('pmoc')}>PMOC</button>}
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      {aba === 'clientes' && (
        <div className="card">
          <div className="titulo-pg" style={{ marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Clientes</h3>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 12 }}>
            <button
              className="btn"
              style={{ minHeight: 44, padding: '0 22px', fontSize: 15 }}
              onClick={() => setClienteAberto(null)}
            >
              Novo cliente
            </button>
          </div>
          <div className="grid cols2" style={{ marginBottom: 12 }}>
            <div>
              <label>Pesquisar cliente</label>
              <input
                className="input"
                value={buscaCliente}
                onChange={(e) => setBuscaCliente(e.target.value)}
                placeholder="Buscar por nome, WhatsApp, documento ou e-mail"
              />
            </div>
            <div>
              <label>Situação</label>
              <select value={statusCliente} onChange={(e) => setStatusCliente(e.target.value as 'todos' | 'ativo' | 'inativo')}>
                <option value="todos">Todos ({clientes.length})</option>
                <option value="ativo">Somente ativos ({totalAtivos})</option>
                <option value="inativo">Somente inativos ({totalInativos})</option>
              </select>
            </div>
          </div>
          <Tabela colunas={['Cliente', 'WhatsApp', 'Documento', 'Status', '']}>
            {paginacaoClientes.itens.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.nome}</strong>
                  {c.email ? <><br /><small>{c.email}</small></> : null}
                </td>
                <td>{c.whatsapp || c.telefone || '-'}</td>
                <td>{c.documento || '-'}</td>
                <td><BadgeStatus status={c.status} /></td>
                <td className="acoes">
                  <button className="btn sec sm" onClick={() => setClienteAberto(c.id)}>Editar completo</button>
                  <BotaoStatus
                    status={c.status}
                    entidade="este cliente"
                    onAlternar={(proximo) => executar(
                      () => api('/cadastros/clientes.php', { method: 'PUT', body: { id: c.id, status: proximo } }),
                      proximo === 'ativo' ? 'Cliente reativado.' : 'Cliente desativado.',
                    )}
                  />
                </td>
              </tr>
            ))}
            {!clientesOrdenados.length && <tr><td colSpan={5} className="vazio">{clientes.length ? 'Nenhum cliente encontrado.' : 'Nenhum cliente cadastrado.'}</td></tr>}
          </Tabela>
          <PaginacaoLista {...paginacaoClientes} />
        </div>
      )}

      {aba === 'tipos_chamado' && (
        <CategoriasSecao
          lista={categorias}
          executar={executar}
          tipo="chamado"
          tituloNovo="Novo tipo de chamado"
          tituloEditar="Editar tipo de chamado"
          labelNome="Tipo de chamado *"
          placeholder="Ex.: Corretivo, Preventivo"
          colunaNome="Tipo de chamado"
          vazio="Nenhum tipo de chamado cadastrado."
          sucessoCriar="Tipo de chamado criado."
          sucessoEditar="Tipo de chamado atualizado."
          entidadeSingular="este tipo de chamado"
          sucessoDesativar="Tipo de chamado desativado."
          sucessoReativar="Tipo de chamado reativado."
        />
      )}

      {/* Mesma tela de /app/ativos, so sem o bloco "Clientes do equipamento". */}
      {aba === 'equipamentos' && <Ativos embutido />}

      {/* Aba PMOC = CONSULTA. Criar plano acontece na ficha do cliente ("Gerar
          plano PMOC"), que e onde vivem os equipamentos e ambientes que ele puxa. */}
      {aba === 'pmoc' && !isSemPmoc && <PmocConsulta />}
    </div>
  );
}

function CadastrosPadrao() {
  const [aba, setAba] = useState<Aba>('unidades');
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [pisos, setPisos] = useState<Piso[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  // "RETIRAR UNIDADE - LOCAL - PISO": as tres abas da hierarquia saem daqui. Os
  // dados nao sao apagados - desligando a permissao, as abas voltam.
  const { user, isSemUnidadeLocalPiso } = useAuth();
  // "Log Auditoria": somente gestor/supervisor.
  const verLogAuditoria = PERFIS_GESTOR_SUPERVISOR.includes(user?.perfil || '');
  const abasVisiveis = ABAS
    .filter((t) => !isSemUnidadeLocalPiso || !['unidades', 'pisos', 'locais'].includes(t.id))
    .filter((t) => t.id !== 'log_auditoria' || verLogAuditoria);

  async function carregar() {
    setErro('');
    const [u, p, l, c, t, cli] = await Promise.all([
      api<Unidade[]>('/cadastros/unidades.php?incluir_inativos=1'),
      // incluir_orfaos=1: só aqui; seletores de OS/equipamento continuam sem órfãos.
      api<Piso[]>('/cadastros/pisos.php?incluir_inativos=1&incluir_orfaos=1'),
      api<Local[]>('/cadastros/locais.php?incluir_inativos=1&incluir_orfaos=1'),
      api<Categoria[]>('/cadastros/categorias.php?incluir_inativos=1'),
      api<Tecnico[]>('/cadastros/tecnicos.php'),
      api<Cliente[]>('/cadastros/clientes.php?incluir_inativos=1'),
    ]);
    setUnidades(u);
    setPisos(p);
    setLocais(l);
    setCategorias(c);
    setTecnicos(t);
    setClientes(cli);
  }

  useEffect(() => {
    carregar().catch((e) => setErro(erroMsg(e)));
  }, []);

  useEffect(() => {
    if (!abasVisiveis.some((t) => t.id === aba)) {
      setAba(abasVisiveis[0]?.id || 'clientes');
    }
  }, [aba, abasVisiveis]);

  async function executar(fn: () => Promise<void>, sucesso: string) {
    setErro('');
    setMsg('');
    try {
      await fn();
      setMsg(sucesso);
      await carregar();
    } catch (e) {
      setErro(erroMsg(e));
    }
  }
  return (
    <div>
      <div className="titulo-pg">
        <h1>Cadastros</h1>
        <button className="btn sec sm" onClick={() => carregar().catch((e) => setErro(erroMsg(e)))}>Atualizar</button>
      </div>

      <p style={{ color: 'var(--texto-suave)', margin: '0 0 14px', fontSize: 14 }}>
        Cadastro não se exclui: as ordens de serviço, equipamentos e relatórios já criados apontam para ele.
        Use <strong>Desativar</strong> — o item deixa de aparecer em novos cadastros e novas OS, e todo o histórico continua intacto.
      </p>

      <div className="tabs" style={{ marginBottom: 14 }}>
        {abasVisiveis.map((t) => (
          <button key={t.id} className={aba === t.id ? 'on' : ''} onClick={() => setAba(t.id)}>{t.label}</button>
        ))}
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      {aba === 'unidades' && <UnidadesSecao lista={unidades} pisos={pisos} locais={locais} executar={executar} />}
      {aba === 'pisos' && <PisosSecao lista={pisos} unidades={unidades} executar={executar} />}
      {aba === 'locais' && <LocaisSecao lista={locais} unidades={unidades} pisos={pisos} executar={executar} />}
      {aba === 'tipos_chamado' && (
        <CategoriasSecao
          lista={categorias}
          executar={executar}
          tipo="chamado"
          tituloNovo="Novo tipo de chamado"
          tituloEditar="Editar tipo de chamado"
          labelNome="Tipo de chamado *"
          placeholder="Ex.: Corretivo, Preventivo"
          colunaNome="Tipo de chamado"
          vazio="Nenhum tipo de chamado cadastrado."
          sucessoCriar="Tipo de chamado criado."
          sucessoEditar="Tipo de chamado atualizado."
          entidadeSingular="este tipo de chamado"
          sucessoDesativar="Tipo de chamado desativado."
          sucessoReativar="Tipo de chamado reativado."
        />
      )}
      {aba === 'tecnicos' && <TecnicosSecao lista={tecnicos} unidades={unidades} executar={executar} />}
      {aba === 'clientes' && <ClientesSecao lista={clientes} executar={executar} />}
      {aba === 'log_auditoria' && verLogAuditoria && <LogAuditoriaSecao />}
    </div>
  );
}

function ClientesSecao({ lista, executar }: { lista: Cliente[]; executar: (fn: () => Promise<void>, s: string) => Promise<void> }) {
  const [form, setForm] = useState<Partial<Cliente>>({});
  // Permissao "CRIACAO DE OS SIMPLIFICADA": abre o cadastro unificado do cliente
  // (Nome -> Endereco -> Documentos -> Equipamentos -> Ambientes). Desligada, esta
  // aba fica exatamente como era.
  const { isOsSimplificadaDisponivel } = useAuth();
  const editando = Boolean(form.id);
  const paginacao = useListaPaginada(lista);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const body = {
      id: form.id,
      nome: (form.nome || '').trim(),
      documento: form.documento || null,
      email: form.email || null,
      telefone: form.telefone || null,
      whatsapp: form.whatsapp || null,
      status: form.status || 'ativo',
    };
    await executar(async () => {
      if (form.id) await api('/cadastros/clientes.php', { method: 'PUT', body });
      else await api('/cadastros/clientes.php', { method: 'POST', body });
      setForm({});
    }, editando ? 'Cliente atualizado.' : 'Cliente criado.');
  }

  return (
    <div className="grid cols2">
      <form className="card" onSubmit={salvar}>
        <h3>{editando ? 'Editar cliente' : 'Novo cliente'}</h3>
        {isOsSimplificadaDisponivel && (
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            Este formulário é o cadastro rápido.{' '}
            <Link to="/app/cliente">Abrir o cadastro completo</Link> para endereço, documentos,
            equipamentos e ambientes na mesma tela.
          </p>
        )}
        <label>Nome *</label>
        <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
        <label>WhatsApp</label>
        <input className="input" value={form.whatsapp || ''} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="Ex.: (11) 99999-9999" />
        <div className="grid cols2">
          <div><label>Telefone</label><input className="input" value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
          <div><label>Documento</label><input className="input" value={form.documento || ''} onChange={(e) => setForm({ ...form, documento: e.target.value })} placeholder="CPF/CNPJ" /></div>
        </div>
        <label>E-mail</label>
        <input className="input" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <label>Status</label>
        <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          <option value="ativo">Ativo</option><option value="inativo">Inativo</option>
        </select>
        <div className="form-actions">
          {editando && <button type="button" className="btn sec" onClick={() => setForm({})}>Cancelar</button>}
          <button className="btn">{editando ? 'Salvar cliente' : 'Adicionar cliente'}</button>
        </div>
      </form>
      <div>
        <Tabela colunas={['Cliente', 'WhatsApp', 'Documento', 'Status', '']}>
          {paginacao.itens.map((c) => (
          <tr key={c.id}>
            <td>
              {isOsSimplificadaDisponivel
                ? <Link to={'/app/cliente/' + c.id}><strong>{c.nome}</strong></Link>
                : <strong>{c.nome}</strong>}
              {c.email ? <><br /><small>{c.email}</small></> : null}
            </td>
            <td>{c.whatsapp || c.telefone || '-'}</td>
            <td>{c.documento || '-'}</td>
            <td><BadgeStatus status={c.status} /></td>
            <td className="acoes">
              <button className="btn sec sm" onClick={() => setForm(c)}>Editar</button>
              <BotaoStatus
                status={c.status}
                entidade="este cliente"
                onAlternar={(proximo) => executar(
                  () => api('/cadastros/clientes.php', { method: 'PUT', body: { id: c.id, status: proximo } }),
                  proximo === 'ativo' ? 'Cliente reativado.' : 'Cliente desativado.',
                )}
              />
            </td>
          </tr>
        ))}
          {!lista.length && <tr><td colSpan={5} className="vazio">Nenhum cliente cadastrado.</td></tr>}
        </Tabela>
        <PaginacaoLista {...paginacao} />
      </div>
    </div>
  );
}

function UnidadesSecao({ lista, pisos, locais, executar }: {
  lista: Unidade[]; pisos: Piso[]; locais: Local[];
  executar: (fn: () => Promise<void>, s: string) => Promise<void>;
}) {
  const { isUnidadeLocalDeleteDisponivel } = useAuth();
  const [form, setForm] = useState<Partial<Unidade>>({});
  const editando = Boolean(form.id);
  const paginacao = useListaPaginada(lista);
  // Dropdown "ver piso/local associado": guarda o id da unidade expandida (uma
  // por vez, pra não empilhar tabelas aninhadas gigantes na tela).
  const [expandidaId, setExpandidaId] = useState<number | null>(null);
  // Seleção múltipla de piso/local dentro do dropdown, pra excluir em lote sem
  // precisar de um botão vermelho em cada linha — chave "piso:id" / "local:id".
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());

  function alternarExpandida(id: number) {
    setExpandidaId((atual) => (atual === id ? null : id));
    setSelecionados(new Set());
  }

  function alternarSelecionado(chave: string) {
    setSelecionados((atual) => {
      const novo = new Set(atual);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const body = { id: form.id, nome: (form.nome || '').trim(), endereco: form.endereco || null, status: form.status || 'ativo' };
    await executar(async () => {
      if (form.id) await api('/cadastros/unidades.php', { method: 'PUT', body });
      else await api('/cadastros/unidades.php', { method: 'POST', body });
      setForm({});
    }, editando ? 'Unidade atualizada.' : 'Unidade criada.');
  }

  const excluir = (path: string, id: number, sucesso: string) => executar(
    () => api(path + '?id=' + id, { method: 'DELETE' }),
    sucesso,
  );

  async function excluirSelecionados() {
    const chaves = Array.from(selecionados);
    if (!chaves.length) return;
    if (!window.confirm(`Excluir ${chaves.length} ${chaves.length === 1 ? 'item selecionado' : 'itens selecionados'}? Apenas os cadastros marcados serão excluídos — os locais de um piso excluído continuam na aba Locais (grupo "Sem piso") para vincular a outro piso; equipamentos e ordens de serviço vinculados NÃO são apagados e as OS continuam no relatório. A exclusão fica registrada em Log Auditoria e pode ser restaurada por lá.`)) return;
    await executar(async () => {
      const falhas: string[] = [];
      for (const chave of chaves) {
        const [tipo, idTexto] = chave.split(':');
        const path = tipo === 'piso' ? '/cadastros/pisos.php' : '/cadastros/locais.php';
        try {
          await api(path + '?id=' + idTexto, { method: 'DELETE' });
        } catch (e) {
          falhas.push(`${tipo === 'piso' ? 'Piso' : 'Local'} #${idTexto}: ${erroMsg(e)}`);
        }
      }
      setSelecionados(new Set());
      if (falhas.length) throw new Error(`${chaves.length - falhas.length} de ${chaves.length} excluído(s). ${falhas.join(' | ')}`);
    }, 'Selecionados excluídos.');
  }

  return (
    <div className="grid cols2">
      <form className="card" onSubmit={salvar}>
        <h3>{editando ? 'Editar unidade' : 'Nova unidade'}</h3>
        <label>Nome *</label>
        <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
        <label>Endereço</label>
        <input className="input" value={form.endereco || ''} onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
        <label>Status</label>
        <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          <option value="ativo">Ativo</option><option value="inativo">Inativo</option>
        </select>
        <div className="form-actions">
          {editando && <button type="button" className="btn sec" onClick={() => setForm({})}>Cancelar</button>}
          <button className="btn">{editando ? 'Salvar unidade' : 'Adicionar unidade'}</button>
        </div>
      </form>
      <div>
      <Tabela colunas={['', 'Nome', 'Endereço', 'Status', '']}>
        {paginacao.itens.map((u) => {
          const pisosDaUnidade = pisos.filter((p) => p.unidade_id === u.id);
          const expandida = expandidaId === u.id;
          return (
            <Fragment key={u.id}>
              <tr>
                <td>
                  {pisosDaUnidade.length > 0 && (
                    <button
                      type="button"
                      className="btn sec sm"
                      title="Ver pisos e locais desta unidade"
                      onClick={() => alternarExpandida(u.id)}
                    >
                      {expandida ? '▾' : '▸'}
                    </button>
                  )}
                </td>
                <td><strong>{u.nome}</strong></td>
                <td>{u.endereco || '-'}</td>
                <td>
                  <BadgeStatus status={u.status} />
                  {isUnidadeLocalDeleteDisponivel && <> <BadgePodeExcluir podeExcluir={u.pode_excluir} /></>}
                </td>
                <td className="acoes">
                  <button className="btn sec sm" onClick={() => setForm(u)}>Editar</button>
                  <BotaoStatus
                    status={u.status}
                    entidade="esta unidade"
                    onAlternar={(proximo) => executar(
                      () => api('/cadastros/unidades.php', { method: 'PUT', body: { id: u.id, status: proximo } }),
                      proximo === 'ativo' ? 'Unidade reativada.' : 'Unidade desativada.',
                    )}
                  />
                  {isUnidadeLocalDeleteDisponivel && (
                    <BotaoExcluir
                      entidade="esta unidade"
                      podeExcluir={u.pode_excluir !== false}
                      aviso={'Os pisos e locais desta unidade NÃO são apagados: continuam nas abas Pisos e Locais (grupo "Sem unidade") e podem ser vinculados a outra unidade.'}
                      onExcluir={() => excluir('/cadastros/unidades.php', u.id, 'Unidade excluída. Os pisos e locais dela continuam na aba Pisos, em "Sem unidade", para vincular a outra unidade.')}
                    />
                  )}
                </td>
              </tr>
              {expandida && (
                <tr>
                  <td />
                  <td colSpan={4}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}>
                      {pisosDaUnidade.map((p) => {
                        const locaisDoPiso = locais.filter((l) => l.piso_id === p.id);
                        const chavePiso = 'piso:' + p.id;
                        return (
                          <div key={p.id} style={{ borderLeft: '2px solid #e6edf7', paddingLeft: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {isUnidadeLocalDeleteDisponivel && (
                                <input
                                  type="checkbox"
                                  checked={selecionados.has(chavePiso)}
                                  disabled={p.pode_excluir === false}
                                  onChange={() => alternarSelecionado(chavePiso)}
                                  title={p.pode_excluir === false
                                    ? 'O sistema não liberou a exclusão deste piso. Use Desativar.'
                                    : 'Selecionar este piso para excluir'}
                                />
                              )}
                              <span><strong>Piso:</strong> {p.nome} <BadgeStatus status={p.status} /> {isUnidadeLocalDeleteDisponivel && <BadgePodeExcluir podeExcluir={p.pode_excluir} />}</span>
                            </div>
                            {locaisDoPiso.length > 0 && (
                              <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {locaisDoPiso.map((l) => {
                                  const chaveLocal = 'local:' + l.id;
                                  return (
                                    <li key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, borderLeft: '2px solid #f1f5f9', paddingLeft: 12 }}>
                                      {isUnidadeLocalDeleteDisponivel && (
                                        <input
                                          type="checkbox"
                                          checked={selecionados.has(chaveLocal)}
                                          disabled={l.pode_excluir === false}
                                          onChange={() => alternarSelecionado(chaveLocal)}
                                          title={l.pode_excluir === false
                                            ? 'O sistema não liberou a exclusão deste local. Use Desativar.'
                                            : 'Selecionar este local para excluir'}
                                        />
                                      )}
                                      <span><strong>Local:</strong> {l.nome} <BadgeStatus status={l.status} /> {isUnidadeLocalDeleteDisponivel && <BadgePodeExcluir podeExcluir={l.pode_excluir} />}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                      {!pisosDaUnidade.length && <p className="muted" style={{ margin: 0 }}>Nenhum piso cadastrado nesta unidade.</p>}
                      {isUnidadeLocalDeleteDisponivel && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                          <button
                            type="button"
                            className="btn sec sm"
                            disabled={!selecionados.size}
                            onClick={excluirSelecionados}
                          >
                            Excluir selecionados{selecionados.size ? ` (${selecionados.size})` : ''}
                          </button>
                          <span className="muted" style={{ fontSize: 12 }}>
                            Marque piso(s)/local(is) acima. Só os itens marcados são excluídos; equipamentos e OS ficam intactos e continuam no relatório.
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
        {!lista.length && <tr><td colSpan={5} className="vazio">Nenhuma unidade cadastrada.</td></tr>}
      </Tabela>
      <PaginacaoLista {...paginacao} />
      </div>
    </div>
  );
}

function PisosSecao({ lista, unidades, executar }: { lista: Piso[]; unidades: Unidade[]; executar: (fn: () => Promise<void>, s: string) => Promise<void> }) {
  const { isUnidadeLocalDeleteDisponivel } = useAuth();
  const [form, setForm] = useState<Partial<Piso>>({});
  const editando = Boolean(form.id);
  const selecao = useSelecaoLote(lista);
  // Pisos cuja unidade foi excluída ficam num grupo à parte ("Sem unidade"), para
  // serem vinculados a outra unidade em vez de recriados.
  const orfaos = selecao.filtrados.filter(pisoOrfao);
  const normais = selecao.filtrados.filter((p) => !pisoOrfao(p));
  const paginacao = useListaPaginada(normais);
  const colSel = isUnidadeLocalDeleteDisponivel ? [''] : [];

  async function excluirSelecionados() {
    const ids = selecao.idsSelecionados;
    if (!ids.length) return;
    if (!window.confirm(`Excluir ${ids.length} ${ids.length === 1 ? 'piso' : 'pisos'}? Os locais deles NÃO são apagados: continuam na aba Locais (grupo "Sem piso"). Equipamentos e OS ficam intactos. Dá para restaurar pelo Log Auditoria.`)) return;
    await executar(async () => {
      const falhas = await excluirEmLote('/cadastros/pisos.php', ids, 'Piso');
      selecao.limpar();
      if (falhas.length) throw new Error(`${ids.length - falhas.length} de ${ids.length} excluído(s). ${falhas.join(' | ')}`);
    }, `${ids.length} ${ids.length === 1 ? 'piso excluído' : 'pisos excluídos'}.`);
  }
  const formOrfao = editando && lista.some((p) => p.id === form.id && pisoOrfao(p));
  const unidadeOriginal = lista.find((p) => p.id === form.id)?.unidade_id;
  const trocandoUnidade = editando && !formOrfao && Boolean(form.unidade_id) && Number(form.unidade_id) !== Number(unidadeOriginal);

  // Piso órfão: abre o editar com a unidade em branco para escolher a nova.
  const editar = (p: Piso) => setForm(pisoOrfao(p) ? { ...p, unidade_id: undefined } : p);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const body = { id: form.id, nome: (form.nome || '').trim(), unidade_id: Number(form.unidade_id), status: form.status || 'ativo' };
    await executar(async () => {
      if (form.id) await api('/cadastros/pisos.php', { method: 'PUT', body });
      else await api('/cadastros/pisos.php', { method: 'POST', body });
      setForm({});
    }, formOrfao ? 'Piso vinculado à nova unidade (os locais dele foram junto).' : editando ? 'Piso atualizado.' : 'Piso criado.');
  }

  const acoes = (p: Piso) => (
    <td className="acoes">
      <button className="btn sec sm" onClick={() => editar(p)}>{pisoOrfao(p) ? 'Vincular a unidade' : 'Editar'}</button>
      <BotaoStatus
        status={p.status}
        entidade="este piso"
        onAlternar={(proximo) => executar(
          () => api('/cadastros/pisos.php', { method: 'PUT', body: { id: p.id, status: proximo } }),
          proximo === 'ativo' ? 'Piso reativado.' : 'Piso desativado.',
        )}
      />
      {isUnidadeLocalDeleteDisponivel && (
        <BotaoExcluir
          entidade="este piso"
          podeExcluir={p.pode_excluir !== false}
          aviso={'Os locais deste piso NÃO são apagados: continuam na aba Locais (grupo "Sem piso") e podem ser vinculados a outro piso.'}
          onExcluir={() => executar(() => api('/cadastros/pisos.php?id=' + p.id, { method: 'DELETE' }), 'Piso excluído. Os locais dele continuam na aba Locais, em "Sem piso", para vincular a outro piso.')}
        />
      )}
    </td>
  );

  return (
    <div className="grid cols2">
      <form className="card" onSubmit={salvar}>
        <h3>{formOrfao ? 'Vincular piso a uma unidade' : editando ? 'Editar piso' : 'Novo piso'}</h3>
        {formOrfao && (
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            A unidade deste piso foi excluída. Escolha a nova unidade — os locais do piso vão junto.
          </p>
        )}
        <label>Unidade *</label>
        <select value={form.unidade_id || ''} onChange={(e) => setForm({ ...form, unidade_id: Number(e.target.value) })} required>
          <option value="">Selecione</option>
          {/* Unidade desativada não é oferecida para um cadastro novo; ao editar, a atual continua na lista. */}
          {unidades.filter((u) => ativo(u.status) || u.id === Number(unidadeOriginal)).map((u) => (
            <option key={u.id} value={u.id}>{u.nome}{ativo(u.status) ? '' : ' (inativa)'}</option>
          ))}
        </select>
        {trocandoUnidade && (
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
            Trocar a unidade move junto todos os locais deste piso.
          </p>
        )}
        <label>Nome *</label>
        <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
        <label>Status</label>
        <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          <option value="ativo">Ativo</option><option value="inativo">Inativo</option>
        </select>
        <div className="form-actions">
          {editando && <button type="button" className="btn sec" onClick={() => setForm({})}>Cancelar</button>}
          <button className="btn">{formOrfao ? 'Vincular piso' : editando ? 'Salvar piso' : 'Adicionar piso'}</button>
        </div>
      </form>
      <div>
      <BarraSelecaoLote
        selecao={selecao}
        placeholder="Pesquisar piso pelo nome"
        podeExcluir={isUnidadeLocalDeleteDisponivel}
        onExcluir={excluirSelecionados}
      />
      {orfaos.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <h3 style={{ margin: '0 0 6px' }}>Sem unidade (unidade excluída)</h3>
          <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
            Estes pisos ficaram sem unidade porque ela foi excluída. Eles não aparecem para abrir OS nem cadastrar
            equipamento até serem vinculados: clique em <strong>Vincular a unidade</strong> para reaproveitar.
          </p>
          <Tabela colunas={[...colSel, 'Piso', 'Unidade', 'Status', '']}>
            {orfaos.map((p) => (
              <tr key={p.id}>
                {isUnidadeLocalDeleteDisponivel && <CelulaSelecao id={p.id} podeExcluir={p.pode_excluir} selecao={selecao} />}
                <td><strong>{p.nome}</strong></td>
                <td><span className="muted">Sem unidade</span></td>
                <td>
                  <BadgeStatus status={p.status} />
                  {isUnidadeLocalDeleteDisponivel && <> <BadgePodeExcluir podeExcluir={p.pode_excluir} /></>}
                </td>
                {acoes(p)}
              </tr>
            ))}
          </Tabela>
        </div>
      )}
      <Tabela colunas={[...colSel, 'Piso', 'Unidade', 'Status', '']}>
        {paginacao.itens.map((p) => (
          <tr key={p.id}>
            {isUnidadeLocalDeleteDisponivel && <CelulaSelecao id={p.id} podeExcluir={p.pode_excluir} selecao={selecao} />}
            <td><strong>{p.nome}</strong></td>
            <td>{nomeUnidade(unidades, p.unidade_id)}</td>
            <td>
              <BadgeStatus status={p.status} />
              {isUnidadeLocalDeleteDisponivel && <> <BadgePodeExcluir podeExcluir={p.pode_excluir} /></>}
            </td>
            {acoes(p)}
          </tr>
        ))}
        {!normais.length && <tr><td colSpan={4 + colSel.length} className="vazio">{selecao.busca.trim() ? 'Nenhum piso encontrado.' : 'Nenhum piso cadastrado.'}</td></tr>}
      </Tabela>
      <PaginacaoLista {...paginacao} />
      </div>
    </div>
  );
}

function LocaisSecao({ lista, unidades, pisos, executar }: { lista: Local[]; unidades: Unidade[]; pisos: Piso[]; executar: (fn: () => Promise<void>, s: string) => Promise<void> }) {
  const { user, isUnidadeLocalDeleteDisponivel } = useAuth();
  const [form, setForm] = useState<Partial<Local>>({});
  const [qrLocal, setQrLocal] = useState<Local | null>(null);
  const editando = Boolean(form.id);
  // Locais cujo piso (ou a unidade do piso) foi excluído ficam num grupo à parte
  // ("Sem piso"), para serem vinculados a outro piso em vez de recriados.
  const selecao = useSelecaoLote(lista);
  const orfaos = selecao.filtrados.filter(localOrfao);
  const normais = selecao.filtrados.filter((l) => !localOrfao(l));
  const paginacao = useListaPaginada(normais);
  const colSel = isUnidadeLocalDeleteDisponivel ? [''] : [];

  async function excluirSelecionados() {
    const ids = selecao.idsSelecionados;
    if (!ids.length) return;
    if (!window.confirm(`Excluir ${ids.length} ${ids.length === 1 ? 'local' : 'locais'}? Equipamentos e OS vinculados NÃO são apagados e as OS continuam no relatório. Dá para restaurar pelo Log Auditoria.`)) return;
    await executar(async () => {
      const falhas = await excluirEmLote('/cadastros/locais.php', ids, 'Local');
      selecao.limpar();
      if (falhas.length) throw new Error(`${ids.length - falhas.length} de ${ids.length} excluído(s). ${falhas.join(' | ')}`);
    }, `${ids.length} ${ids.length === 1 ? 'local excluído' : 'locais excluídos'}.`);
  }
  const formOrfao = editando && lista.some((l) => l.id === form.id && localOrfao(l));
  const original = lista.find((l) => l.id === form.id);
  // Só oferece pai ATIVO num cadastro novo; ao editar, mantém o atual visível.
  // Piso órfão (unidade excluída) nunca é oferecido: vincule o piso antes, na aba Pisos.
  const unidadesOfertadas = unidades.filter((u) => ativo(u.status) || u.id === Number(original?.unidade_id));
  const pisosDaUnidade = (form.unidade_id ? pisos.filter((p) => p.unidade_id === Number(form.unidade_id)) : pisos)
    .filter((p) => !pisoOrfao(p))
    .filter((p) => ativo(p.status) || p.id === Number(original?.piso_id));

  // Local órfão: abre o editar com unidade/piso em branco para escolher o novo.
  const editar = (l: Local) => setForm(localOrfao(l) ? { ...l, unidade_id: undefined, piso_id: undefined } : l);
  const situacaoOrfao = (l: Local) => (flag(l.piso_excluido)
    ? 'Sem piso (piso excluído)'
    : `Piso "${nomePiso(pisos, l.piso_id)}" sem unidade (unidade excluída)`);
  const acoes = (l: Local) => (
    <td className="acoes">
      {l.qr_code && <button className="btn sec sm" onClick={() => setQrLocal(l)}>QR</button>}
      <button className="btn sec sm" onClick={() => editar(l)}>{localOrfao(l) ? 'Vincular a piso' : 'Editar'}</button>
      <BotaoStatus
        status={l.status}
        entidade="este local"
        onAlternar={(proximo) => executar(
          () => api('/cadastros/locais.php', { method: 'PUT', body: { id: l.id, status: proximo } }),
          proximo === 'ativo' ? 'Local reativado.' : 'Local desativado.',
        )}
      />
      {isUnidadeLocalDeleteDisponivel && (
        <BotaoExcluir
          entidade="este local"
          podeExcluir={l.pode_excluir !== false}
          onExcluir={() => executar(() => api('/cadastros/locais.php?id=' + l.id, { method: 'DELETE' }), 'Local excluído.')}
        />
      )}
    </td>
  );

  // QR embute o tenant (empresa_id) — mesmo padrão do QR de equipamentos.
  const qrUrl = (l: Local) => {
    const codigo = (l.qr_code || '').trim();
    const empresaId = user?.empresa_id ?? '';
    const base = `${window.location.origin}${window.location.pathname}`;
    return `${base}#/app/qr/${empresaId}/${encodeURIComponent(codigo)}`;
  };

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const body = {
      id: form.id,
      nome: (form.nome || '').trim(),
      unidade_id: Number(form.unidade_id),
      piso_id: Number(form.piso_id),
      status: form.status || 'ativo',
    };
    await executar(async () => {
      if (form.id) await api('/cadastros/locais.php', { method: 'PUT', body });
      else await api('/cadastros/locais.php', { method: 'POST', body });
      setForm({});
    }, formOrfao ? 'Local vinculado ao novo piso.' : editando ? 'Local atualizado.' : 'Local criado.');
  }

  return (
    <div className="grid cols2">
      <form className="card" onSubmit={salvar}>
        <h3>{formOrfao ? 'Vincular local a um piso' : editando ? 'Editar local' : 'Novo local'}</h3>
        {formOrfao && (
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            {original && flag(original.piso_excluido)
              ? 'O piso deste local foi excluído. Escolha a unidade e o novo piso.'
              : 'A unidade do piso deste local foi excluída. Escolha a unidade e o novo piso — ou vincule o piso inteiro a outra unidade na aba Pisos.'}
          </p>
        )}
        <label>Unidade *</label>
        <select value={form.unidade_id || ''} onChange={(e) => setForm({ ...form, unidade_id: Number(e.target.value), piso_id: undefined })} required>
          <option value="">Selecione</option>
          {unidadesOfertadas.map((u) => <option key={u.id} value={u.id}>{u.nome}{ativo(u.status) ? '' : ' (inativa)'}</option>)}
        </select>
        <label>Piso *</label>
        <select value={form.piso_id || ''} onChange={(e) => setForm({ ...form, piso_id: Number(e.target.value) })} required>
          <option value="">Selecione</option>
          {pisosDaUnidade.map((p) => <option key={p.id} value={p.id}>{p.nome}{ativo(p.status) ? '' : ' (inativo)'}</option>)}
        </select>
        <label>Nome *</label>
        <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
        <label>Status</label>
        <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          <option value="ativo">Ativo</option><option value="inativo">Inativo</option>
        </select>
        <div className="form-actions">
          {editando && <button type="button" className="btn sec" onClick={() => setForm({})}>Cancelar</button>}
          <button className="btn">{formOrfao ? 'Vincular local' : editando ? 'Salvar local' : 'Adicionar local'}</button>
        </div>
      </form>
      <div>
      <BarraSelecaoLote
        selecao={selecao}
        placeholder="Pesquisar local pelo nome"
        podeExcluir={isUnidadeLocalDeleteDisponivel}
        onExcluir={excluirSelecionados}
      />
      {orfaos.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <h3 style={{ margin: '0 0 6px' }}>Sem piso (piso excluído)</h3>
          <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
            Estes locais ficaram sem piso/unidade porque o pai foi excluído. Eles não aparecem para abrir OS nem
            cadastrar equipamento até serem vinculados: clique em <strong>Vincular a piso</strong> para reaproveitar.
          </p>
          <Tabela colunas={[...colSel, 'Local', 'Situação', 'Status', '']}>
            {orfaos.map((l) => (
              <tr key={l.id}>
                {isUnidadeLocalDeleteDisponivel && <CelulaSelecao id={l.id} podeExcluir={l.pode_excluir} selecao={selecao} />}
                <td><strong>{l.nome}</strong></td>
                <td><span className="muted">{situacaoOrfao(l)}</span></td>
                <td>
                  <BadgeStatus status={l.status} />
                  {isUnidadeLocalDeleteDisponivel && <> <BadgePodeExcluir podeExcluir={l.pode_excluir} /></>}
                </td>
                {acoes(l)}
              </tr>
            ))}
          </Tabela>
        </div>
      )}
      <Tabela colunas={[...colSel, 'Local', 'Unidade', 'Piso', 'Status', '']}>
        {paginacao.itens.map((l) => (
          <tr key={l.id}>
            {isUnidadeLocalDeleteDisponivel && <CelulaSelecao id={l.id} podeExcluir={l.pode_excluir} selecao={selecao} />}
            <td><strong>{l.nome}</strong></td>
            <td>{nomeUnidade(unidades, l.unidade_id)}</td>
            <td>{nomePiso(pisos, l.piso_id)}</td>
            <td>
              <BadgeStatus status={l.status} />
              {isUnidadeLocalDeleteDisponivel && <> <BadgePodeExcluir podeExcluir={l.pode_excluir} /></>}
            </td>
            {acoes(l)}
          </tr>
        ))}
        {!normais.length && <tr><td colSpan={5 + colSel.length} className="vazio">{selecao.busca.trim() ? 'Nenhum local encontrado.' : 'Nenhum local cadastrado.'}</td></tr>}
      </Tabela>
      <PaginacaoLista {...paginacao} />
      </div>

      {qrLocal && (
        <QRPrintModal
          code={qrLocal.qr_code || ''}
          title={qrLocal.nome}
          subtitle={`${nomeUnidade(unidades, qrLocal.unidade_id)} · ${nomePiso(pisos, qrLocal.piso_id)}`}
          value={qrUrl(qrLocal)}
          onClose={() => setQrLocal(null)}
        />
      )}
    </div>
  );
}

function CategoriasSecao({
  lista,
  executar,
  tipo,
  tituloNovo,
  tituloEditar,
  labelNome,
  placeholder,
  colunaNome,
  vazio,
  sucessoCriar,
  sucessoEditar,
  entidadeSingular,
  sucessoDesativar,
  sucessoReativar,
}: {
  lista: Categoria[];
  executar: (fn: () => Promise<void>, s: string) => Promise<void>;
  tipo: 'ativo' | 'chamado';
  tituloNovo: string;
  tituloEditar: string;
  labelNome: string;
  placeholder: string;
  colunaNome: string;
  vazio: string;
  sucessoCriar: string;
  sucessoEditar: string;
  entidadeSingular: string;
  sucessoDesativar: string;
  sucessoReativar: string;
}) {
  const itens = lista;
  const [form, setForm] = useState<Partial<Categoria>>({ tipo });
  const editando = Boolean(form.id);
  const paginacao = useListaPaginada(itens);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const body = { id: form.id, nome: (form.nome || '').trim(), tipo, status: form.status || 'ativo' };
    await executar(async () => {
      if (form.id) await api('/cadastros/categorias.php', { method: 'PUT', body });
      else await api('/cadastros/categorias.php', { method: 'POST', body });
      setForm({ tipo });
    }, editando ? sucessoEditar : sucessoCriar);
  }

  return (
    <div className="grid cols2">
      <form className="card" onSubmit={salvar}>
        <h3>{editando ? tituloEditar : tituloNovo}</h3>
        <label>{labelNome}</label>
        <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder={placeholder} required />
        <label>Status</label>
        <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          <option value="ativo">Ativo</option><option value="inativo">Inativo</option>
        </select>
        <div className="form-actions">
          {editando && <button type="button" className="btn sec" onClick={() => setForm({ tipo })}>Cancelar</button>}
          <button className="btn">{editando ? 'Salvar' : 'Adicionar'}</button>
        </div>
      </form>
      <div>
      <Tabela colunas={[colunaNome, 'Status', '']}>
        {paginacao.itens.map((c) => (
          <tr key={c.id}>
            <td><strong>{c.nome}</strong></td>
            <td><BadgeStatus status={c.status} /></td>
            <td className="acoes">
              <button className="btn sec sm" onClick={() => setForm(c)}>Editar</button>
              <BotaoStatus
                status={c.status}
                entidade={entidadeSingular}
                onAlternar={(proximo) => executar(
                  () => api('/cadastros/categorias.php', { method: 'PUT', body: { id: c.id, status: proximo } }),
                  proximo === 'ativo' ? sucessoReativar : sucessoDesativar,
                )}
              />
            </td>
          </tr>
        ))}
        {!itens.length && <tr><td colSpan={3} className="vazio">{vazio}</td></tr>}
      </Tabela>
      <PaginacaoLista {...paginacao} />
      </div>
    </div>
  );
}

function TecnicosSecao({ lista, unidades, executar }: { lista: Tecnico[]; unidades: Unidade[]; executar: (fn: () => Promise<void>, s: string) => Promise<void> }) {
  const [form, setForm] = useState<Partial<Tecnico> & { senha?: string }>({});
  const [selecionadas, setSelecionadas] = useState<number[]>([]);
  const editando = Boolean(form.id);
  const paginacao = useListaPaginada(lista);

  function alternarUnidade(id: number) {
    setSelecionadas((atuais) => atuais.includes(id) ? atuais.filter((u) => u !== id) : [...atuais, id]);
  }

  function editar(t: Tecnico) {
    setForm({ ...t, senha: '' });
    setSelecionadas(t.unidades || []);
  }

  function limpar() {
    setForm({});
    setSelecionadas([]);
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = {
      id: form.id,
      nome: (form.nome || '').trim(),
      telefone: form.telefone || null,
      whatsapp: form.whatsapp || null,
      valor_hora: num(form.valor_hora),
      status: form.status || 'ativo',
      unidades: selecionadas,
    };
    if (!editando) {
      body.email = (form.email || '').trim();
      body.senha = form.senha || '';
    } else if (form.senha) {
      body.senha = form.senha;
    }
    await executar(async () => {
      if (form.id) await api('/cadastros/tecnicos.php', { method: 'PUT', body });
      else await api('/cadastros/tecnicos.php', { method: 'POST', body });
      limpar();
    }, editando ? 'Técnico atualizado.' : 'Técnico criado.');
  }


  async function alternarStatus(t: Tecnico) {
    const proximoStatus = t.status === 'ativo' ? 'inativo' : 'ativo';
    const acao = proximoStatus === 'ativo' ? 'reativar' : 'desativar';
    if (!window.confirm(`Deseja ${acao} este tecnico?`)) return;
    await executar(
      () => api('/cadastros/tecnicos.php', { method: 'PUT', body: { id: t.id, status: proximoStatus } }),
      proximoStatus === 'ativo' ? 'Técnico reativado.' : 'Técnico desativado.',
    );
  }

  return (
    <div className="grid cols2">
      <form className="card" onSubmit={salvar}>
        <h3>{editando ? 'Editar técnico' : 'Novo técnico'}</h3>
        <label>Nome *</label>
        <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
        <label>E-mail *</label>
        <input className="input" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={editando} required={!editando} />
        <div className="grid cols2">
          <div><label>Telefone</label><input className="input" value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
          <div><label>WhatsApp</label><input className="input" value={form.whatsapp || ''} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></div>
        </div>
        <label>Valor da Hora (R$)</label>
        <input
          className="input"
          inputMode="decimal"
          value={valorMoeda(form.valor_hora)}
          onChange={(e) => setForm({ ...form, valor_hora: parseMoedaInput(e.target.value) })}
          placeholder="R$ 0,00"
        />
        <label>{editando ? 'Nova senha (opcional)' : 'Senha *'}</label>
        <input className="input" type="password" value={form.senha || ''} onChange={(e) => setForm({ ...form, senha: e.target.value })} required={!editando} />
        <label>Unidades atendidas</label>
        <div className="check-grid">
          {unidades.map((u) => (
            <label key={u.id} className="check-line">
              <input type="checkbox" checked={selecionadas.includes(u.id)} onChange={() => alternarUnidade(u.id)} />
              {u.nome}
            </label>
          ))}
        </div>
        <div className="form-actions">
          {editando && <button type="button" className="btn sec" onClick={limpar}>Cancelar</button>}
          <button className="btn">{editando ? 'Salvar técnico' : 'Adicionar técnico'}</button>
        </div>
      </form>
      <Tabela colunas={['Técnico', 'Contato', 'Valor/Hora', 'Unidades', 'Status', '']}>
        {paginacao.itens.map((t) => (
          <tr key={t.id}>
            <td><strong>{t.nome}</strong><br /><small>{t.email}</small></td>
            <td>{t.whatsapp || t.telefone || '-'}</td>
            <td>{valorMoeda(t.valor_hora || 0)}</td>
            <td>{(t.unidades || []).map((id) => nomeUnidade(unidades, id)).join(', ') || '-'}</td>
            <td><span className="badge">{t.status}</span></td>
            <td className="acoes">
              <button className="btn sec sm" onClick={() => editar(t)}>Editar</button>
              <button
                className={`btn ${t.status === 'ativo' ? 'danger' : 'sec'} sm`}
                onClick={() => alternarStatus(t)}
              >
                {t.status === 'ativo' ? 'Desativar' : 'Ativar'}
              </button>
            </td>
          </tr>
        ))}
        {!lista.length && <tr><td colSpan={6} className="vazio">Nenhum técnico cadastrado.</td></tr>}
      </Tabela>
      <PaginacaoLista {...paginacao} />
    </div>
  );
}

function Tabela({ colunas, children }: { colunas: string[]; children: ReactNode }) {
  return (
    <div className="tabela-wrap">
      <table>
        <thead><tr>{colunas.map((c, i) => <th key={c + i}>{c}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const normalizarBusca = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Abas Pisos e Locais: busca por nome + seleção múltipla para excluir de uma vez.
// "Selecionar todos" marca tudo o que a busca mostra (todas as páginas).
function useSelecaoLote<T extends { id: number; nome: string; pode_excluir?: boolean }>(lista: T[]) {
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const termo = normalizarBusca(busca);
  const filtrados = termo ? lista.filter((x) => normalizarBusca(x.nome || '').includes(termo)) : lista;
  const selecionaveis = filtrados.filter((x) => x.pode_excluir !== false).map((x) => x.id);
  const todosMarcados = selecionaveis.length > 0 && selecionaveis.every((id) => selecionados.has(id));
  // Só conta o que ainda existe na lista (item excluído some da seleção).
  const idsSelecionados = lista.filter((x) => selecionados.has(x.id)).map((x) => x.id);
  const alternar = (id: number) => setSelecionados((atual) => {
    const novo = new Set(atual);
    if (novo.has(id)) novo.delete(id);
    else novo.add(id);
    return novo;
  });
  const alternarTodos = () => setSelecionados((atual) => {
    const novo = new Set(atual);
    selecionaveis.forEach((id) => (todosMarcados ? novo.delete(id) : novo.add(id)));
    return novo;
  });
  const limpar = () => setSelecionados(new Set());
  return { busca, setBusca, filtrados, selecionados, idsSelecionados, todosMarcados, qtdSelecionaveis: selecionaveis.length, alternar, alternarTodos, limpar };
}

// DELETE de vários ids, 5 por vez. Devolve as mensagens de quem falhou.
async function excluirEmLote(path: string, ids: number[], rotulo: string): Promise<string[]> {
  const falhas: string[] = [];
  for (let i = 0; i < ids.length; i += 5) {
    await Promise.all(ids.slice(i, i + 5).map((id) => api(path + '?id=' + id, { method: 'DELETE' })
      .catch((e) => { falhas.push(`${rotulo} #${id}: ${erroMsg(e)}`); })));
  }
  return falhas;
}

function BarraSelecaoLote({ selecao, placeholder, podeExcluir, onExcluir }: {
  selecao: ReturnType<typeof useSelecaoLote>;
  placeholder: string;
  podeExcluir: boolean;
  onExcluir: () => void;
}) {
  const qtd = selecao.idsSelecionados.length;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <input
        className="input"
        style={{ flex: '1 1 220px', margin: 0 }}
        placeholder={placeholder}
        value={selecao.busca}
        onChange={(e) => selecao.setBusca(e.target.value)}
      />
      {podeExcluir && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0, whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={selecao.todosMarcados}
              disabled={!selecao.qtdSelecionaveis}
              onChange={selecao.alternarTodos}
            />
            Selecionar todos ({selecao.qtdSelecionaveis})
          </label>
          <button type="button" className="btn sec sm" disabled={!qtd} onClick={onExcluir}>
            Excluir selecionados{qtd ? ` (${qtd})` : ''}
          </button>
        </>
      )}
    </div>
  );
}

function CelulaSelecao({ id, podeExcluir, selecao }: {
  id: number;
  podeExcluir?: boolean;
  selecao: { selecionados: Set<number>; alternar: (id: number) => void };
}) {
  return (
    <td style={{ width: 28 }}>
      <input
        type="checkbox"
        checked={selecao.selecionados.has(id)}
        disabled={podeExcluir === false}
        onChange={() => selecao.alternar(id)}
        title={podeExcluir === false ? 'O sistema não liberou a exclusão deste item. Use Desativar.' : 'Selecionar para excluir'}
      />
    </td>
  );
}
